import { useEffect, useState } from 'react';

export type ValidationStatus = 'success' | 'error';

export type SuccessFunc = (label?: string) => true;
export type ErrorFunc = (label?: string) => false;

export type ValidateFunc<T> = (value: T, success: SuccessFunc, error: ErrorFunc) => boolean;

export type FormFieldState<T> = {
  value: T;
  validationStatus?: ValidationStatus;
  validationLabel?: string;
  setValue: (value: T) => void;
  validate: () => boolean;
  setValidationError: (errorLabel: string) => void;
  removeValidation: () => void;
  hasError: boolean;
  isValidated: boolean;
};

const useFormField = <T>(initialValue: T | (() => T), validateFn?: ValidateFunc<T>): FormFieldState<T> => {
  const [value, setValue] = useState(initialValue);
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>();
  const [validationLabel, setValidationLabel] = useState<string>();

  const setValidationState = (status?: ValidationStatus, label?: string) => {
    setValidationStatus(status);
    setValidationLabel(label);
  };

  // Clear validation status when value changes
  useEffect(() => {
    setValidationState();
  }, [value]);

  const validate = () =>
    validateFn
      ? validateFn(
          value,
          (label) => {
            setValidationState('success', label);
            return true;
          },
          (label) => {
            setValidationState('error', label);
            return false;
          }
        )
      : true;

  /**
   * Function to set validation error in the form field
   * @param errorLabel - Label to be shown as error
   */
  const setValidationError = (errorLabel: string) => {
    setValidationState('error', errorLabel);
  };

  /**
   * Function to remove the existing validation from the form field
   */
  const removeValidation = () => {
    setValidationState();
  };

  const hasError = validationStatus === 'error';

  const isValidated = !!validationStatus;

  return {
    value,
    validationStatus,
    validationLabel,
    setValue,
    validate,
    setValidationError,
    removeValidation,
    hasError,
    isValidated,
  };
};

export default useFormField;
